import React from 'react';
import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import ImageSearchOutlinedIcon from '@mui/icons-material/ImageSearchOutlined';
import { Box, Button, ButtonBase, CircularProgress, Tooltip, Typography } from '@mui/material';

import {
  usePendingImageQuickPicks,
  type PendingImageQuickPickCandidate,
} from '../hooks/usePendingImageQuickPicks';

import type { UnsplashImageSelection } from './UnsplashImageLibraryDialog';

import { readTrimmedString, buildPlaceholderKey } from '@/shared/utils/documentImagePlaceholders';

export interface DocumentImagePlaceholderUiActions {
  onActivatePlaceholder?: (placeholderKey: string) => void;
  onResolvePlaceholder?: (placeholderKey: string, selection: UnsplashImageSelection) => void;
  onBrowseLibrary?: (placeholderKey: string, searchTerm: string) => void;
}

interface DocumentImagePlaceholderExtensionOptions {
  getUiActions: () => DocumentImagePlaceholderUiActions | null;
}

function readNodeViewPos(getPos: NodeViewProps['getPos']): number | null {
  try {
    const pos = getPos();
    return typeof pos === 'number' ? pos : null;
  } catch {
    return null;
  }
}

const Thumbnail: React.FC<{
  candidate: PendingImageQuickPickCandidate;
  onSelect: (selection: UnsplashImageSelection) => void;
}> = ({ candidate, onSelect }) => (
  <Tooltip title={`${candidate.title} — ${candidate.subtitle}`} placement="top" arrow>
    <ButtonBase
      onClick={() => onSelect(candidate.selection)}
      sx={{
        width: 56,
        height: 56,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid #dfe3eb',
        flexShrink: 0,
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          transform: 'scale(1.08)',
        },
      }}
    >
      <img
        src={candidate.previewSrc}
        alt={candidate.title}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </ButtonBase>
  </Tooltip>
);

const ImagePlaceholderNodeView: React.FC<NodeViewProps> = ({
  node,
  selected,
  extension,
  getPos,
}) => {
  const attrs = (node.attrs ?? {}) as Record<string, unknown>;
  const imageQuery = readTrimmedString(attrs.imageQuery);
  const placeholderId = readTrimmedString(attrs.id);
  const placeholderPos = placeholderId ? null : readNodeViewPos(getPos);
  const placeholderKey = React.useMemo(
    () => buildPlaceholderKey(placeholderId || null, placeholderPos ?? 0),
    [placeholderId, placeholderPos]
  );
  const uiActions =
    (extension.options as DocumentImagePlaceholderExtensionOptions).getUiActions?.() ?? null;
  const quickPicks = usePendingImageQuickPicks(
    imageQuery,
    uiActions != null && imageQuery.trim().length > 0
  );
  const allCandidates = React.useMemo(
    () => [...quickPicks.photos, ...quickPicks.symbols].slice(0, 4),
    [quickPicks.photos, quickPicks.symbols]
  );
  const isLoading = quickPicks.photosLoading || quickPicks.symbolsLoading;

  React.useEffect(() => {
    if (!selected) return;
    uiActions?.onActivatePlaceholder?.(placeholderKey);
  }, [placeholderKey, selected, uiActions]);

  return (
    <NodeViewWrapper className="document-image-placeholder-node" contentEditable={false}>
      <Box
        sx={{
          border: '1px dashed',
          borderColor: selected ? '#1a73e8' : '#9aa0a6',
          borderRadius: 2,
          p: 1.5,
          my: '6pt',
          bgcolor: selected ? '#eef4ff' : '#f8f9fa',
          transition: 'border-color 0.15s, background-color 0.15s',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          {imageQuery.length > 0 ? `Image: "${imageQuery}"` : 'Pending image — no search term'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {isLoading && allCandidates.length === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Loading suggestions…
              </Typography>
            </Box>
          )}

          {allCandidates.map((candidate) => (
            <Thumbnail
              key={candidate.id}
              candidate={candidate}
              onSelect={(selection) => uiActions?.onResolvePlaceholder?.(placeholderKey, selection)}
            />
          ))}

          <Button
            variant="outlined"
            size="small"
            startIcon={<ImageSearchOutlinedIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => uiActions?.onBrowseLibrary?.(placeholderKey, imageQuery)}
            disabled={!uiActions?.onBrowseLibrary}
            sx={{
              height: 56,
              minWidth: 0,
              px: 1.5,
              fontSize: '0.75rem',
              textTransform: 'none',
              borderColor: '#dfe3eb',
            }}
          >
            More…
          </Button>
        </Box>
      </Box>
    </NodeViewWrapper>
  );
};

const DocumentImagePlaceholderExtension = Node.create<DocumentImagePlaceholderExtensionOptions>({
  name: 'imagePlaceholder',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  defining: true,

  addOptions() {
    return {
      getUiActions: () => null,
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        rendered: false,
      },
      imageQuery: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-image-query'),
        renderHTML: (attributes) => {
          const imageQuery = readTrimmedString(attributes.imageQuery);
          return imageQuery.length > 0 ? { 'data-image-query': imageQuery } : {};
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="image-placeholder"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': 'image-placeholder' }, HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImagePlaceholderNodeView);
  },
});

export default DocumentImagePlaceholderExtension;
